import React, { useRef, useEffect } from 'react';
import { Container, Typography, Box, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import BrushIcon from '@mui/icons-material/Brush';
import CodeIcon from '@mui/icons-material/Code';
import SpeedIcon from '@mui/icons-material/Speed';
import BugReportIcon from '@mui/icons-material/BugReport';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import './WebProcess.css';

const processes = [
  {
    icon: <EmojiObjectsIcon sx={{ fontSize: 40 }} />,
    title: 'Discovery & Planning',
    description: 'Understanding your vision and planning the perfect strategy.',
    color: '#FF6B6B'
  },
  {
    icon: <BrushIcon sx={{ fontSize: 40 }} />,
    title: 'UI/UX Design',
    description: 'Creating intuitive and engaging user experiences.',
    color: '#4ECDC4'
  },
  {
    icon: <CodeIcon sx={{ fontSize: 40 }} />,
    title: 'Development',
    description: 'Building with modern technologies and best practices.',
    color: '#45B7D1'
  },
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Optimization',
    description: 'Ensuring fast loading and smooth performance.',
    color: '#96C93D'
  },
  {
    icon: <BugReportIcon sx={{ fontSize: 40 }} />,
    title: 'Testing',
    description: 'Rigorous testing across devices and browsers.',
    color: '#A569BD'
  },
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
    title: 'Launch',
    description: 'Deploying and monitoring for success.',
    color: '#5D6D7E'
  }
];

const WebProcess = () => {
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -340 : 340;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="web-process">
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="web-process-header"
        >
          <Typography variant="h2" className="web-process-title">
            Our Development Process
          </Typography>
          <Typography variant="subtitle1" className="web-process-subtitle">
            A streamlined approach to bringing your web vision to life
          </Typography>
        </motion.div>

        <motion.div 
          className="process-container"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="web-process-timeline-line"></div>
          
          <div className="web-scroll-button left">
            <IconButton onClick={() => scroll('left')} className="web-scroll-icon-button">
              <KeyboardArrowLeftIcon />
            </IconButton>
          </div>
          
          <div className="web-process-steps" ref={scrollContainerRef}>
            {processes.map((process, index) => (
              <motion.div
                key={index}
                className="web-process-step"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="web-step-number" style={{ background: process.color }}>
                  {index + 1}
                </div>
                <div className="web-step-content">
                  <div className="web-step-icon" style={{ color: process.color }}>
                    {process.icon}
                  </div>
                  <Typography variant="h6" className="web-step-title">
                    {process.title}
                  </Typography>
                  <Typography variant="body2" className="web-step-description">
                    {process.description}
                  </Typography>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="web-scroll-button right">
            <IconButton onClick={() => scroll('right')} className="web-scroll-icon-button">
              <KeyboardArrowRightIcon />
            </IconButton>
          </div>
        </motion.div>
      </Container>
    </section>
  );
};

export default WebProcess; 